import { useState } from 'react';
import { useJoveBreakpoint, useLocalTranslation } from '@jove-frontend/uikit';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { LegalTocItem } from './utils';

interface LegalDocumentTocProps {
  items: LegalTocItem[];
  activeId: string;
  onSelect: (id: string) => void;
}

export default function LegalDocumentToc(props: LegalDocumentTocProps): JSX.Element | null {
  const { items, activeId, onSelect } = props;

  /**
   * state
   */
  const [expanded, setExpanded] = useState(false);
  const { $t } = useLocalTranslation();
  const { isMobileLayout } = useJoveBreakpoint();

  /**
   * methods
   */
  const selectSectionAction = (id: string) => {
    setExpanded(false);
    onSelect(id);
  };

  const toggleExpandedAction = () => {
    setExpanded((previous) => !previous);
  };

  if (items.length === 0) return null;

  const list = (
    <Box component="ul" className="m-0 list-none p-0">
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <Box key={item.id} component="li" className="mb-1">
            <button
              type="button"
              onClick={() => selectSectionAction(item.id)}
              className={clsx(
                'block w-full cursor-pointer border-l-2 bg-transparent py-1.5 pl-3 text-left text-sm leading-5 transition-colors',
                isActive
                  ? 'border-brand-primary text-brand-primary font-semibold'
                  : 'border-grey-10 text-grey-50 hover:border-grey-50 hover:text-gray-dark'
              )}
            >
              {item.text}
            </button>
          </Box>
        );
      })}
    </Box>
  );

  if (isMobileLayout) {
    return (
      <Accordion expanded={expanded} onChange={toggleExpandedAction} className="border-grey-10 mb-6 rounded-lg border shadow-none before:hidden">
        <AccordionSummary className="min-h-[44px]">
          <Typography className="text-true-black text-sm font-semibold">{$t('page:legalDocument.contents')}</Typography>
        </AccordionSummary>
        <AccordionDetails className="pt-0">{list}</AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box component="nav" className="sticky top-24 max-h-[calc(100vh-8rem)] w-60 shrink-0 overflow-y-auto pr-2">
      <Typography className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-50">{$t('page:legalDocument.contents')}</Typography>
      {list}
    </Box>
  );
}
